/**
 * @fileoverview Command Parser Schemas
 *
 * Zod schemas for natural language command parsing system.
 * These schemas define the structure for command input, output, and validation.
 */

import { z } from '@hono/zod-openapi';
import { ITEM_STATUSES } from './item.js';

/**
 * Maximum input length for command parsing (characters)
 */
export const MAX_COMMAND_LENGTH = 200;

/**
 * Approved intents that can be processed by the command parser
 */
export const APPROVED_INTENTS = {
  // Reading/Navigation
  READ_NEXT_ITEM: 'read_next_item',
  READ_ITEM: 'read_item',
  SHOW_ITEMS: 'show_items',

  // Item Management
  CLASSIFY_ITEM: 'classify_item',
  ADD_TO_ISSUE: 'add_to_issue',
  UPDATE_STATUS: 'update_status',
  ARCHIVE_ITEM: 'archive_item',

  // Issue Management
  CREATE_ISSUE: 'create_issue',
  SHOW_ISSUES: 'show_issues',

  // Search
  SEARCH: 'search',

  // Dashboard/UI Control
  EXPAND_WIDGET: 'expand_widget',
  COLLAPSE_WIDGET: 'collapse_widget',

  // Unknown/Unsupported
  UNKNOWN: 'unknown'
};

/**
 * Valid item categories for classification
 */
export const ITEM_CATEGORIES = ['news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'];

// ITEM_STATUSES imported from item.js (canonical source for item status values)

/**
 * Parameter schemas for each intent - defines expected parameter names and types
 */
export const INTENT_PARAMETER_SCHEMAS = {
  // Reading/Navigation intents
  [APPROVED_INTENTS.READ_NEXT_ITEM]: {
    filter: { type: 'string', description: 'Filter for next item (e.g., "next", "unread")', optional: true },
    category: { type: 'string', enum: ITEM_CATEGORIES, description: 'Filter by specific category', optional: true },
    status: { type: 'string', enum: ITEM_STATUSES, description: 'Filter by specific status', optional: true }
  },

  [APPROVED_INTENTS.READ_ITEM]: {
    itemId: { type: 'string', description: 'Specific item ID to read', required: true },
    format: { type: 'string', description: 'Output format (e.g., "summary", "full")', optional: true }
  },

  [APPROVED_INTENTS.SHOW_ITEMS]: {
    category: { type: 'string', enum: ITEM_CATEGORIES, description: 'Filter by category', optional: true },
    status: { type: 'string', enum: ITEM_STATUSES, description: 'Filter by status', optional: true },
    limit: { type: 'number', description: 'Number of items to show', optional: true },
    domain: { type: 'string', description: 'Filter by source domain', optional: true }
  },

  // Item Management intents
  [APPROVED_INTENTS.CLASSIFY_ITEM]: {
    itemId: { type: 'string', description: 'Item ID to classify', optional: true },
    category: { type: 'string', enum: ITEM_CATEGORIES, description: 'Category to assign', required: true }
  },

  [APPROVED_INTENTS.ADD_TO_ISSUE]: {
    itemId: { type: 'string', description: 'Item ID to add to issue', required: true },
    issueNumber: { type: 'number', description: 'Issue number to add item to', required: true }
  },

  [APPROVED_INTENTS.UPDATE_STATUS]: {
    itemId: { type: 'string', description: 'Item ID to update', optional: true },
    status: { type: 'string', enum: ITEM_STATUSES, description: 'New status to set', required: true }
  },

  [APPROVED_INTENTS.ARCHIVE_ITEM]: {
    itemId: { type: 'string', description: 'Item ID to archive', optional: true }
  },

  // Issue Management intents
  [APPROVED_INTENTS.CREATE_ISSUE]: {
    title: { type: 'string', description: 'Issue title', optional: true },
    issueNumber: { type: 'number', description: 'Specific issue number', optional: true }
  },

  [APPROVED_INTENTS.SHOW_ISSUES]: {
    status: { type: 'string', enum: ['published', 'unpublished'], description: 'Filter by issue status', optional: true },
    limit: { type: 'number', description: 'Number of issues to show', optional: true }
  },

  // Search intents
  [APPROVED_INTENTS.SEARCH]: {
    query: { type: 'string', description: 'Search query', required: true },
    category: { type: 'string', enum: ITEM_CATEGORIES, description: 'Filter by category', optional: true },
    domain: { type: 'string', description: 'Filter by domain', optional: true }
  },

  // Dashboard/UI Control intents
  [APPROVED_INTENTS.EXPAND_WIDGET]: {
    widgetId: { type: 'string', description: 'Widget ID to expand (e.g., mainCanvas, timeWidget, statsGrid, sidePanel)', optional: true },
    widgetName: { type: 'string', description: 'Widget name/description (e.g., "graph", "chart", "time", "metrics", "status")', optional: true }
  },

  [APPROVED_INTENTS.COLLAPSE_WIDGET]: {
    widgetId: { type: 'string', description: 'Widget ID to collapse', optional: true }
  },

  // Unknown intent (fallback)
  [APPROVED_INTENTS.UNKNOWN]: {}
};

/**
 * Command input schema
 */
export const CommandInputSchema = z.object({
  command: z.string()
    .min(1, 'Command cannot be empty')
    .max(MAX_COMMAND_LENGTH, `Command must be ${MAX_COMMAND_LENGTH} characters or less`)
    .trim()
    .openapi({
      description: 'Natural language command to parse',
      example: 'read me the next unread article'
    }),
  disableCache: z.boolean().optional().openapi({
    description: 'Bypass intent cache for fresh LLM responses (slower but tests raw performance)',
    example: false
  }),
  context: z.object({
    currentItemId: z.string().nullish().openapi({
      description: 'ID of the currently selected item',
      example: 'item-abc123'
    }),
    currentIssueId: z.string().nullish().openapi({
      description: 'ID of the currently selected issue',
      example: 'issue-00042'
    }),
    userId: z.string().nullish().openapi({
      description: 'ID of the user executing the command',
      example: 'user-123'
    }),
    websocketId: z.string().nullish().openapi({
      description: 'WebSocket connection ID for real-time responses',
      example: 'ws-dashboard-456'
    }),
    source: z.string().nullish().openapi({
      description: 'Source of the command (e.g., live-dashboard, chat)',
      example: 'live-dashboard'
    }),
    voice: z.object({
      id: z.string().openapi({
        description: 'Voice ID for text-to-speech responses',
        example: 'Joanna'
      }),
      engine: z.string().optional().openapi({
        description: 'TTS engine to use',
        example: 'generative'
      }),
      speed: z.string().optional().openapi({
        description: 'Speech speed',
        example: 'medium'
      })
    }).optional().openapi({
      description: 'Voice preferences for TTS responses'
    })
  }).optional().openapi({
    description: 'Optional context information for command execution'
  })
}).openapi({
  description: 'Input for command parsing',
  example: {
    command: 'read me the next unread article',
    context: {
      userId: 'user-123',
      websocketId: 'ws-dashboard-456',
      source: 'live-dashboard',
      voice: {
        id: 'Joanna',
        engine: 'generative',
        speed: 'medium'
      }
    }
  }
});



/**
 * Parsed command output schema
 */
export const ParsedCommandSchema = z.object({
  intent: z.string().openapi({
    description: 'The parsed intent of the command',
    example: 'read_next_item'
  }),
  parameters: z.record(z.string(), z.any()).optional().openapi({
    description: 'Parameters extracted from the command',
    example: { filter: 'next', category: 'aws' }
  }),
  confidence: z.number().min(0).max(1).openapi({
    description: 'Confidence score of the parsing (0-1)',
    example: 0.95
  }),
  explanation: z.string().optional().openapi({
    description: 'Human-readable explanation of the parsed command',
    example: 'User wants to read the next unread article'
  }),
  requiresContext: z.boolean().optional().openapi({
    description: 'Whether this command requires context to execute',
    example: false
  }),
  errors: z.array(z.string()).optional().openapi({
    description: 'Array of error messages if parsing encountered issues',
    example: ['Invalid category provided']
  })
}).openapi({
  description: 'Parsed command with intent and parameters',
  example: {
    intent: 'read_next_item',
    parameters: { filter: 'next' },
    confidence: 0.95,
    explanation: 'User wants to read the next unread article',
    requiresContext: false
  }
});

/**
 * Command assessment result schema (for quick assessment without full parsing)
 */
export const CommandAssessmentSchema = z.object({
  supported: z.boolean().openapi({
    description: 'Whether the command is likely to be supported',
    example: true
  }),
  confidence: z.number().min(0).max(1).openapi({
    description: 'Confidence score of the assessment (0-1)',
    example: 0.8
  }),
  matchedKeywords: z.array(z.string()).optional().openapi({
    description: 'Keywords that were recognized in the command',
    example: ['read', 'next', 'article']
  }),
  reason: z.string().openapi({
    description: 'Explanation of the assessment result',
    example: 'Contains recognized keywords'
  })
}).openapi({
  description: 'Quick assessment of command supportability',
  example: {
    supported: true,
    confidence: 0.8,
    matchedKeywords: ['read', 'next', 'article'],
    reason: 'Contains recognized keywords'
  }
});

/**
 * Extended parsed command schema with API metadata (for route responses)
 */
export const ParsedCommandResponseSchema = ParsedCommandSchema.extend({
  usage: z.object({
    inputTokens: z.number().openapi({
      description: 'Number of input tokens used',
      example: 45
    }),
    outputTokens: z.number().openapi({
      description: 'Number of output tokens generated',
      example: 23
    }),
    totalTokens: z.number().openapi({
      description: 'Total tokens used',
      example: 68
    })
  }).optional().openapi({
    description: 'Token usage information from the AI model'
  }),
  modelId: z.string().optional().openapi({
    description: 'ID of the AI model used for parsing',
    example: 'anthropic.claude-3-haiku-20240307-v1:0'
  }),
  latencyMs: z.number().optional().openapi({
    description: 'Processing latency in milliseconds',
    example: 750
  })
}).openapi({
  description: 'Parsed command response with API metadata',
  example: {
    intent: 'read_next_item',
    parameters: { filter: 'next' },
    confidence: 0.95,
    explanation: 'User wants to read the next unread article',
    requiresContext: false,
    usage: {
      inputTokens: 45,
      outputTokens: 23,
      totalTokens: 68
    },
    modelId: 'anthropic.claude-3-haiku-20240307-v1:0',
    latencyMs: 750
  }
});